import type { ForwardedRef, CSSProperties } from 'react';
import React, { useRef, useCallback, forwardRef } from 'react';

import Icon from '@/components/common/Icon';
import * as SC from '@/components/common/Input.style';
import { checkRegExpRule } from '@/utils/checkRegExpRule';

interface InputProps {
  /**
   * 아이콘 등이 들어올 수 있습니다.
   */
  children?: React.ReactNode;

  /**
   * 입력 전에 보여줄 메세지를 적어주세요.
   */
  placeholder?: string;

  /**
   *  입력된 값입니다. 초기 값을 줄 수 있습니다.
   */
  value?: string | number;

  /**
   * 인풋의 테마를 설정할 수 있습니다.
   */
  theme?: 'primary' | 'secondary';

  /**
   * 인풋의 타입을 설정할 수 있습니다.
   */
  type?: string;

  /**
   *  커스텀을 위해 클래스명을 제공합니다.
   */
  className?: string;

  /**
   * border-radius 를 조절할 수 있습니다.
   */
  borderRadius?: string;

  /**
   * width 를 조절할 수 있습니다.
   */
  width?: string;

  /**
   * height 를 조절할 수 있습니다.
   */
  height?: string;

  /**
   * padding 을 조절할 수 있습니다.
   */
  padding?: string;

  /**
   * close button width 를 조절할 수 있습니다.
   */
  closeBtnWidth?: string;

  /**
   * 아이콘과 함께 보여주는 경우에 설정합니다.
   */
  withIcon?: boolean;

  /**
   *  입력을 지울 수 있는 버튼을 함께 보여주는 경우에 설정합니다.
   */
  withClose?: boolean;

  /**
   *  속성이 비밀번호인 경우에 view 옵션을 보여줄 지 설정합니다.
   */
  withView?: boolean;

  /**
   *  활성화 여부를 설정할 때 사용하는 동작입니다.
   */
  disabled?: boolean;

  /**
   *  읽기 전용 속성을 설정할 때 사용하는 동작입니다.
   */
  readOnly?: boolean;
  /**
   *  숫자만 입력되어야 할 때 사용하는 동작입니다.
   */
  numberOnly?: boolean;
  /**
   *  Input의 폰트 사이즈를 설정할 수 있습니다.
   */
  fontSize?: string;
  /**
   *  Input의 name 속성을 지정할 수 있습니다.
   */
  name?: string;
  /**
   *  에러에 대한 상태가 필요한 경우에 사용합니다.
   * */
  isError?: boolean;
  errorMessage?: string;
  helpMessage?: string;
  /**
   *  입력 길이를 제한할 경우에 사용합니다.
   * */
  maxLength?: number;
  /**
   *  인풋의 라벨이 필요한 경우에 사용합니다.
   */
  label?: string;
  /**
   *  인풋의 라벨에 필수값 표기가 필요한 경우에 사용합니다.
   */
  isRequired?: boolean;
  /**
   * 인풋의 자동완성 기능을 OFF하고 싶을 때 사용합니다.
   */
  withAutoComplete?: boolean;
  /**
   *  onChange 이벤트가 발생하지 않는, 드롭다운과 똑같이 생긴 Input이 필요한 경우에 사용합니다.
   */
  onClick?: () => void;
  /**
   *  입력 시에 발생하는 동작입니다.
   */
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;

  /**
   *  입력을 지울 수 있는 동작입니다.
   */
  onClose?: () => void;

  /**
   *  blur 되었을 경우에 동작입니다.
   */
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;

  /**
   * keyDown 되었을 경우 동작입니다.
   */
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;

  /**
   *  비밀번호 표시 / 가리기를 조정하는 동작입니다.
   */
  handleShowPassword?: () => void;

  wrapperStyle?: CSSProperties;
}

const Input = forwardRef(
  (
    {
      placeholder,
      value,
      theme = 'primary',
      type = 'text',
      className,
      borderRadius,
      width,
      height,
      padding,
      withIcon,
      withClose,
      closeBtnWidth,
      withView,
      disabled,
      readOnly,
      numberOnly,
      fontSize = '14px',
      name,
      isError,
      // errorMessage,
      // helpMessage,
      maxLength,
      children,
      label,
      isRequired,
      withAutoComplete = true,
      onClick,
      onChange,
      onClose,
      onBlur,
      // onKeyDown,
      handleShowPassword,
      wrapperStyle,
    }: InputProps,
    ref: ForwardedRef<HTMLInputElement>,
  ) => {
    const buttonRef = useRef<HTMLButtonElement>(null);

    const closeAndBlur = useCallback(() => {
      onClose && onClose();
      buttonRef?.current?.blur();
    }, [onClose]);

    return (
      <SC.Container
        onClick={() => {
          if (!onClick) {
            return;
          }
          onClick();
        }}
        style={wrapperStyle}
      >
        {label && (
          <SC.StyledLabel htmlFor={name}>
            {label}
            {isRequired && <SC.Required>*</SC.Required>}
          </SC.StyledLabel>
        )}
        <SC.InputBox
          css={[{ borderRadius, width, height, padding }, withIcon && SC.withIconStyle]}
          isError={isError ?? false}
          className={className}
          data-theme={theme}
        >
          <SC.StyledInput
            value={value}
            placeholder={placeholder}
            onChange={(e) => {
              const currentValue = e.currentTarget.value;

              if (numberOnly && currentValue && !checkRegExpRule('ONLY_DIGIT', currentValue.replace(/ /g, ''))) {
                return;
              }

              if (typeof maxLength !== 'undefined' && currentValue.length > maxLength) {
                return;
              }

              if (numberOnly) {
                e.currentTarget.value = currentValue.replace(/ /g, '');
              }

              onChange && onChange(e);
            }}
            disabled={disabled}
            readOnly={readOnly}
            type={type}
            fontSize={fontSize}
            onBlur={onBlur}
            name={name}
            maxLength={maxLength}
            withView={withView ?? false}
            id={name}
            data-testid={name}
            ref={ref}
            autoComplete={withAutoComplete ? undefined : 'new-password'}
          />
          <SC.ChildrenIcon>{children}</SC.ChildrenIcon>
          {/* {children} */}
          {withView && (
            <SC.ViewBtn type='button' onClick={handleShowPassword}>
              <Icon icon={type === 'text' ? 'IcViewOn' : 'IcViewOff'} color='#999999' />
            </SC.ViewBtn>
          )}
          {withClose && value && (
            <SC.CloseBtn
              onClick={closeAndBlur}
              ref={buttonRef}
              closeBtnWidth={closeBtnWidth ?? closeBtnWidth}
              withView={withView}
              type='button'
            >
              <Icon icon='IcDelete' size='16px' />
            </SC.CloseBtn>
          )}
          {onClick && (
            <SC.ViewBtn>
              <Icon icon='IcXsDropdown' />
            </SC.ViewBtn>
          )}
        </SC.InputBox>
        {/* {errorMessage && (
                    <StyledWarnMessage
                        message={errorMessage}
                        fontSize="12px"
                        isError
                    />
                )} */}
        {/* {!errorMessage && helpMessage && (
                    <HelpMessage>{helpMessage}</HelpMessage>
                )} */}
      </SC.Container>
    );
  },
);

export default Input;
